import { useTranslation } from 'common/hooks/useTranslation';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { useAnalyticalFieldMapping } from 'src/core/modules/accounting-integration/hooks/useAnalyticalFieldMapping';

import { useDeleteCustomFieldMutation } from './useDeleteCustomFieldMutation';
import { CustomFieldDeletionModal } from '../../components/CustomFieldDeletionModal';
import { type CustomField } from '../../models/customField';

type Props = {
  isOpen: boolean;
  customField: CustomField;
  onClose(): void;
};

export const CustomFieldDeletionModalContainer = ({
  isOpen,
  customField,
  onClose,
}: Props) => {
  const [deleteCustomField] = useDeleteCustomFieldMutation(customField.id);
  const { t } = useTranslation('global');
  const { pushNotif } = useNotifications();

  const mapping = useAnalyticalFieldMapping({
    fieldType: 'customField',
    field: customField,
  });

  const handleSubmit = async () => {
    try {
      await deleteCustomField();
      pushNotif({
        type: NotificationType.Success,
        message: t('customFields.deletion.successRequest'),
      });

      if (mapping.isMapSyncEnabled && mapping.fieldMapping?.internalId) {
        mapping.onSaveChanges({
          previousMapping: {
            ...mapping.fieldMapping,
            shouldRemoveSyncSetting: true,
          },
          syncMode: undefined,
          exportSettings: undefined,
        });
      }

      onClose();
    } catch {
      pushNotif({
        type: NotificationType.Danger,
        message: t('customFields.deletion.errorRequest'),
      });
    }
  };

  return (
    <CustomFieldDeletionModal
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={handleSubmit}
      showMappedFieldSubtitle={
        mapping.isMapSyncEnabled && !!mapping.fieldMapping?.internalId
      }
    />
  );
};
